import { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserQRCodeReader, IScannerControls } from '@zxing/browser';
import { logger } from '@/lib/logger';

const FEATURE = 'qr-scanner';

export interface CameraDevice {
  deviceId: string;
  label: string;
}

export function useQrScanner(active: boolean, onResult: (text: string) => void) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const readerRef = useRef<BrowserQRCodeReader | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const stop = useCallback(() => {
    controlsRef.current?.stop();
    controlsRef.current = null;
  }, []);

  // Start scanning — uses selectedCameraId if set, otherwise undefined (default camera)
  useEffect(() => {
    if (!active || !videoRef.current) {
      stop();
      return;
    }

    const reader = new BrowserQRCodeReader();
    readerRef.current = reader;
    let cancelled = false;

    const deviceId = selectedCameraId ?? undefined;

    reader
      .decodeFromVideoDevice(deviceId, videoRef.current, (result, err) => {
        if (cancelled) return;
        if (result) {
          onResultRef.current(result.getText());
        }
        if (err && err.name !== 'NotFoundException') {
          logger.warn('QR scan error', { feature: FEATURE, error: err.message });
        }
      })
      .then((controls) => {
        if (cancelled) {
          controls.stop();
          return;
        }
        controlsRef.current = controls;
        setError(null);

        // Now that camera permission is granted, enumerate devices (labels are populated)
        BrowserQRCodeReader.listVideoInputDevices()
          .then((devices) => {
            if (cancelled) return;
            setCameras(
              devices.map((d) => ({
                deviceId: d.deviceId,
                label: d.label || `Camera ${d.deviceId.slice(0, 8)}`,
              }))
            );
          })
          .catch(() => {});
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error('Failed to start QR scanner', err, { feature: FEATURE, route: 'start' });
        if (err?.name === 'NotAllowedError') {
          setError('Camera permission denied. Please allow camera access to scan QR codes.');
        } else {
          setError('Unable to access camera. Please check your device settings.');
        }
      });

    return () => {
      cancelled = true;
      stop();
    };
  }, [active, selectedCameraId, stop]);

  const selectCamera = useCallback((deviceId: string) => {
    stop();
    setSelectedCameraId(deviceId);
  }, [stop]);

  return { videoRef, error, cameras, selectedCameraId, selectCamera };
}
